import { useQuery } from "@tanstack/react-query";
import { agentsCatalogueQueryOptions } from "../queries/agents-queries";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";

function AtIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="m4.5 4.5 7 7m0-7-7 7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Single UX: the @-button now just dispatches a `strands:insert-mention`
// event that InputBar handles by inserting `@` at the caret and opening the
// inline mention menu — same flow as typing @ directly.
function openMentionMenu() {
  window.dispatchEvent(new CustomEvent("strands:insert-mention"));
}

// Nothing to pick (only the implicit default agent exists): don't insert a
// stray `@` into the message — instead open the drawer's empty state, which
// explains the situation and links to agent settings.
function openAgentHelp() {
  window.dispatchEvent(new CustomEvent("strands:open-agent-help"));
}

export function HeroAgentPill() {
  const pendingAgentRef = useAgentDraftStore((s) => s.pendingRef);
  const setPendingAgentRef = useAgentDraftStore((s) => s.setPendingRef);
  const { data } = useQuery(agentsCatalogueQueryOptions());
  const agents = data?.agents ?? [];
  const defaultAgent = data?.defaultAgent ?? null;

  const isOverride =
    !!pendingAgentRef && !!defaultAgent && pendingAgentRef !== defaultAgent.ref;
  const activeAgent = isOverride
    ? agents.find((a) => a.ref === pendingAgentRef) ?? null
    : null;

  // Agents the user can actually pick = catalogue minus the implicit default.
  // Mirrors InputBar's `sortedAgents` pool. Nothing to pick → disable @.
  const hasSelectableAgents = defaultAgent
    ? agents.some((a) => a.ref !== defaultAgent.ref)
    : agents.length > 0;

  const clearOverride = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setPendingAgentRef(null);
  };

  if (isOverride && activeAgent) {
    return (
      <div className="hero-agent-pill-wrap">
        <button
          type="button"
          className="hero-agent-pill"
          onClick={openMentionMenu}
        >
          <span className="hero-agent-pill-prefix">Агент:</span>
          <span className="hero-agent-pill-name">{activeAgent.label || activeAgent.name}</span>
          <span
            role="button"
            tabIndex={0}
            className="hero-agent-pill-clear"
            aria-label="Сбросить агента"
            title="Сбросить агента"
            onClick={clearOverride}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                clearOverride(e);
              }
            }}
          >
            <CloseIcon />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="hero-agent-pill-wrap">
      <button
        type="button"
        className="hero-agent-at-btn"
        onClick={hasSelectableAgents ? openMentionMenu : openAgentHelp}
        title="Выбрать агента"
        aria-label="Выбрать агента"
      >
        <AtIcon />
      </button>
    </div>
  );
}
